import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import {
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  FolderIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  MagnifyingGlassIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../contexts/PageTitleContext';
import apiService, { AutoEncryptFolder } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import toast from 'react-hot-toast';

interface Device {
  id: string;
  name: string;
  type: string;
  hardware_id?: string;
  status?: string;
  created_at: string;
  updated_at?: string;
  last_seen?: string;
  system_info?: Record<string, any>;
  metadata?: Record<string, any>;
}

const UserDetails: React.FC = () => {
  const { t } = useTranslation('userDetails');
  const { id } = useParams<{ id: string }>();
  const { user: currentUser, refreshUserData } = useAuth();
  const { setPageTitle } = usePageTitle();
  const { setHasUnsavedChanges, setOnSave, setIsSubmitting } = useUnsavedChanges();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'details' | 'devices'>('details');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState<Device | null>(null);
  const [deletingDeviceId, setDeletingDeviceId] = useState<string | null>(null);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const [deletePhase, setDeletePhase] = useState<string | null>(null);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const [showCompanyEditModal, setShowCompanyEditModal] = useState(false);
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const [availableCompanies, setAvailableCompanies] = useState<any[]>([]);
  const [isUpdatingCompany, setIsUpdatingCompany] = useState(false);
  const navigate = useNavigate();
  
  // User preferences state (for OwnershipTransferEnabled)
  const [userPreferences, setUserPreferences] = useState<{ ownership_transfer_enabled?: boolean } | null>(null);
  const [originalPreferences, setOriginalPreferences] = useState<{ ownership_transfer_enabled?: boolean } | null>(null);
  const [isUpdatingPreferences, setIsUpdatingPreferences] = useState(false);
  
  // Original user data for change detection
  const [originalUserData, setOriginalUserData] = useState<{
    name: string;
    email: string;
    role: 'user' | 'admin' | 'sysadmin' | 'translator';
    translatorLanguage: '' | 'en' | 'de' | 'es' | 'fr' | 'fi' | 'tr';
    mode: 'full' | 'reader';
    is_active: boolean;
  } | null>(null);
  
  // Editable fields state
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    role: 'user' as 'user' | 'admin' | 'sysadmin' | 'translator',
    translatorLanguage: '' as '' | 'en' | 'de' | 'es' | 'fr' | 'fi' | 'tr',
    mode: 'full' as 'full' | 'reader',
    is_active: true,
  });
  
  const handleSaveRef = useRef<(() => Promise<void>) | undefined>(undefined);

  // Fetch user details
  const { data: user, isLoading: userLoading, error: userError } = useQuery({
    queryKey: ['user', id],
    queryFn: async () => {
      const userData = await apiService.getUserById(id!);
      return userData;
    },
    enabled: !!id,
    retry: false, // Don't retry on error
  });

  // Fetch user preferences (only for admins)
  const { data: preferencesData, isLoading: preferencesLoading } = useQuery({
    queryKey: ['user-preferences', id],
    queryFn: async () => {
      const prefs = await apiService.getUserPreferencesForUser(id!);
      return prefs;
    },
    enabled: !!id && (currentUser?.role === 'sysadmin' || currentUser?.role === 'admin'),
    retry: false,
  });

  // Fetch company usages for mode-edit availability (when user has company and viewer is admin/sysadmin)
  const { data: companyUsages } = useQuery({
    queryKey: ['company-usages', user?.company_id],
    queryFn: () => apiService.getCompanyUsages(user!.company_id!),
    enabled: !!user?.company_id && (currentUser?.role === 'sysadmin' || currentUser?.role === 'admin'),
  });

  // Fetch company data for domain validation (when assigning company_auditor role)
  const { data: companyData } = useQuery({
    queryKey: ['company', user?.company_id],
    queryFn: () => apiService.getCompany(user!.company_id!),
    enabled: !!user?.company_id && (currentUser?.role === 'sysadmin' || currentUser?.role === 'admin'),
  });
  const allowedEmailDomains = useMemo((): string[] => {
    const s = companyData?.settings;
    if (!s) return [];
    try {
      const parsed = typeof s === 'string' ? JSON.parse(s) : s;
      const arr = parsed?.allowed_email_domains;
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  }, [companyData?.settings]);
  const emailDomainInAllowed = (email: string, domains: string[]): boolean => {
    const part = email.trim().split('@')[1]?.toLowerCase();
    if (!part) return false;
    return domains.some((d) => (d || '').trim().toLowerCase() === part);
  };

  // Fetch company asset protection for delete-modal copy (when target has company)
  const { data: companyAssetProtection } = useQuery({
    queryKey: ['company-asset-protection', user?.company_id],
    queryFn: () => apiService.getCompanyAssetProtectionSettings(user!.company_id!),
    enabled: !!user?.company_id && (currentUser?.role === 'sysadmin' || currentUser?.role === 'admin') && showDeleteUserModal,
  });

  // Update preferences state when data is fetched
  useEffect(() => {
    if (preferencesData?.preferences) {
      const prefs = {
        ownership_transfer_enabled: preferencesData.preferences.ownership_transfer_enabled ?? true,
      };
      setUserPreferences(prefs);
      setOriginalPreferences(prefs);
    }
  }, [preferencesData]);
  
  // Redirect to users page if company admin can't access user (user has no company)
  useEffect(() => {
    if (userError && currentUser?.role === 'admin') {
      const errorStatus = (userError as any)?.response?.status;
      const errorMessage = (userError as any)?.response?.data?.error || (userError as any)?.message || '';
      
      // Check if error is due to user having no company (403/500 with "no company" message)
      if (errorStatus === 403 || errorStatus === 500 || errorMessage.includes('no company') || errorMessage.includes('forbidden')) {
        console.log('🔄 USER DETAILS: Company admin cannot access user (likely no company), redirecting to users page', {
          errorStatus,
          errorMessage,
        });
        toast.error(t('toast.notAuthorized'));
        navigate('/users');
      }
    }
  }, [userError, currentUser, navigate, t]);
  
  // Check if current user can edit this user
  const canEdit = useMemo(() => {
    if (!currentUser || !user) return false;
    // Sysadmin can edit anyone
    if (currentUser.role === 'sysadmin') return true;
    // Admin can edit users in their company or companies they manage
    // If admin successfully loaded the user (passed backend permission check), they can edit
    // Backend enforces: admin can only edit users from their own company or managed companies
    if (currentUser.role === 'admin') {
      // If user was successfully loaded, admin has access (backend already validated)
      // This covers both own company and managed companies
      return true;
    }
    return false;
  }, [currentUser, user]);

  // Inactive users (e.g. suspended org admins) cannot change other users' mode or status
  const canEditModeAndStatus = canEdit && (currentUser?.is_active !== false);

  // Fetch companies for company selection modal
  const fetchCompanies = async () => {
    try {
      const response = await apiService.get('/companies');
      setAvailableCompanies(response.data.companies || []);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
      toast.error(t('toast.loadCompaniesFailed'));
    }
  };

  // Handle company update
  const handleCompanyUpdate = async (companyId: string | null) => {
    if (!id) return;
    
    setIsUpdatingCompany(true);
    try {
      const payload = {
        company_id: companyId ?? null, // Explicitly send null instead of undefined
      };
      
      console.log('🔧 USER DETAILS: Updating user company', {
        userId: id,
        companyId: companyId,
        payload: payload,
        payloadStringified: JSON.stringify(payload),
        companyIdType: typeof companyId,
        companyIdValue: companyId,
        isNull: companyId === null,
        isUndefined: companyId === undefined,
      });
      
      await apiService.updateUser(id, payload);
      
      console.log('✅ USER DETAILS: Company update request sent successfully', {
        userId: id,
        companyId: companyId,
      });
      
      toast.success(t('toast.companyUpdated'));
      setShowCompanyEditModal(false);
      
      // If company admin removed user's company (set to null), redirect to users page
      // because admin can no longer view users without a company
      if (companyId === null && currentUser?.role === 'admin') {
        console.log('🔄 USER DETAILS: Company admin removed user company, redirecting to users page');
        // Small delay to show the success toast, then redirect
        setTimeout(() => {
          navigate('/users');
        }, 1000);
        return; // Don't invalidate queries since we're redirecting
      }
      
      // For sysadmin or when assigning company (not removing), refresh user data
      queryClient.invalidateQueries({ queryKey: ['user', id] });
    } catch (error: any) {
      console.error('❌ USER DETAILS: Failed to update company:', {
        userId: id,
        companyId: companyId,
        error: error,
        errorMessage: error?.message,
        errorResponse: error?.response?.data,
      });
      toast.error(error.response?.data?.error || t('toast.companyUpdateFailed'));
    } finally {
      setIsUpdatingCompany(false);
    }
  };

  // Filter companies by search term and permissions
  // For admins: only show companies they manage (own company + companies where managed_by matches their company_id)
  // For sysadmins: show all companies
  const filteredCompanies = useMemo(() => {
    let companies = availableCompanies;
    
    // Additional frontend filter for admins (backend already filters, but this is extra safety)
    if (currentUser?.role === 'admin' && currentUser?.company_id) {
      companies = companies.filter((company: any) => 
        company.id === currentUser.company_id || 
        company.managed_by === currentUser.company_id
      );
    }
    
    // Apply search filter
    if (!companySearchTerm.trim()) return companies;
    const term = companySearchTerm.toLowerCase();
    return companies.filter((company: any) =>
      company.name.toLowerCase().includes(term) ||
      (company.domain && company.domain.toLowerCase().includes(term))
    );
  }, [availableCompanies, companySearchTerm, currentUser?.role, currentUser?.company_id]);

  // Fetch devices for this specific user (backend already filters web devices)
  const { data: userDevices = [], isLoading: devicesLoading } = useQuery({
    queryKey: ['user-devices', id],
    queryFn: async () => {
      const response = await apiService.get(`/devices/user/${id}`);
      return response.data || [];
    },
    enabled: !!id && (currentUser?.role === 'sysadmin' || currentUser?.role === 'admin'),
  });

  // Fetch all folders
  const { data: allFolders = [], isLoading: foldersLoading } = useQuery({
    queryKey: ['all-folders'],
    queryFn: async () => {
      return apiService.getAutoEncryptFolders();
    },
    enabled: !!id && (currentUser?.role === 'sysadmin' || currentUser?.role === 'admin'),
  });

  // Group folders by device for this user
  const foldersByDevice = useMemo(() => {
    if (!id || !allFolders.length) return {};
    
    const grouped: Record<string, AutoEncryptFolder[]> = {};
    allFolders.forEach((folder: AutoEncryptFolder) => {
      if (folder.user_id === id && folder.device_id) {
        if (!grouped[folder.device_id]) {
          grouped[folder.device_id] = [];
        }
        grouped[folder.device_id].push(folder);
      }
    });
    return grouped;
  }, [allFolders, id]);

  // Set page title and initialize form data
  useEffect(() => {
    if (user) {
      setPageTitle(user.name || user.email, t('subtitle'));
      // Parse role - check if it's a translator role (translator_xx)
      let role: 'user' | 'admin' | 'sysadmin' | 'translator' = 'user';
      let translatorLanguage: '' | 'en' | 'de' | 'es' | 'fr' | 'fi' | 'tr' = '';
      
      if (user.role && user.role.startsWith('translator_')) {
        role = 'translator';
        const langCode = user.role.replace('translator_', '');
        if (['en', 'de', 'es', 'fr', 'fi', 'tr'].includes(langCode)) {
          translatorLanguage = langCode as 'en' | 'de' | 'es' | 'fr' | 'fi' | 'tr';
        }
      } else {
        role = (user.role as 'user' | 'admin' | 'sysadmin') || 'user';
      }
      
      const mode = (user.mode === 'reader' ? 'reader' : 'full') as 'full' | 'reader';
      const initialData = {
        name: user.name || '',
        email: user.email || '',
        role,
        translatorLanguage,
        mode,
        is_active: user.is_active ?? true,
      };
      setEditFormData(initialData);
      setOriginalUserData(initialData);
    }
  }, [user, setPageTitle, t]);
  
  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    if (!originalUserData) return false;
    return (
      editFormData.name !== originalUserData.name ||
      editFormData.email !== originalUserData.email ||
      editFormData.role !== originalUserData.role ||
      (editFormData.role === 'translator' && editFormData.translatorLanguage !== originalUserData.translatorLanguage) ||
      editFormData.mode !== originalUserData.mode ||
      editFormData.is_active !== originalUserData.is_active
    );
  }, [editFormData, originalUserData]);
  
  // Handle save
  const handleSave = useCallback(async () => {
    if (!user || !id || !hasChanges) return;
    
    // Validate translator language if translator role is selected
    if (editFormData.role === 'translator' && !editFormData.translatorLanguage) {
      toast.error(t('toast.selectTranslatorLanguage'));
      return;
    }

    // Domain validation for company_auditor role (admin only; sysadmin exempt):
    // - Editing a company_auditor's email must keep it in the company domain
    const isAuditorBeingEdited = (user?.role as string) === 'company_auditor';
    if (isAuditorBeingEdited && currentUser?.role !== 'sysadmin' && user?.company_id) {
      if (allowedEmailDomains.length > 0 && !emailDomainInAllowed(editFormData.email, allowedEmailDomains)) {
        toast.error('Company auditors must have an email address matching the company domain.');
        return;
      }
    }
    
    try {
      setIsSubmitting(true);
      // Format role: if translator, combine with language (e.g., translator_es)
      const roleToSave = editFormData.role === 'translator' 
        ? `translator_${editFormData.translatorLanguage}` 
        : editFormData.role;
      
      await apiService.updateUser(id, {
        name: editFormData.name,
        email: editFormData.email,
        role: roleToSave as any,
        mode: editFormData.mode,
        is_active: editFormData.is_active,
      });
      toast.success(t('toast.userUpdated'));
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['company-usages', user?.company_id] });
      // Update original data after save
      setOriginalUserData({ 
        name: editFormData.name,
        email: editFormData.email,
        role: editFormData.role,
        translatorLanguage: editFormData.translatorLanguage || '',
        mode: editFormData.mode,
        is_active: editFormData.is_active,
      });
      setHasUnsavedChanges(false);
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('toast.userUpdateFailed'));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    user,
    id,
    editFormData,
    hasChanges,
    queryClient,
    setIsSubmitting,
    setHasUnsavedChanges,
    t,
    allowedEmailDomains,
    currentUser?.role,
  ]);
  
  // Update save ref
  useEffect(() => {
    handleSaveRef.current = handleSave;
  }, [handleSave]);
  
  // Set up header save button when there are changes
  useEffect(() => {
    if (hasChanges && canEdit) {
      setOnSave(() => () => handleSaveRef.current?.());
      setHasUnsavedChanges(true);
    } else {
      setOnSave(null);
      setHasUnsavedChanges(false);
    }
    return () => {
      setOnSave(null);
      setHasUnsavedChanges(false);
    };
  }, [hasChanges, canEdit, setOnSave, setHasUnsavedChanges]);
  
  // Handle tab change with unsaved changes check
  const handleTabChange = (newTab: 'details' | 'devices') => {
    if (hasChanges && canEdit) {
      setPendingNavigation(() => () => setActiveTab(newTab));
      setShowUnsavedChangesModal(true);
    } else {
      setActiveTab(newTab);
    }
  };
  
  // Handle navigation away with unsaved changes
  const handleDiscardChanges = () => {
    if (user && originalUserData) {
      setEditFormData({ ...originalUserData });
    }
    setHasUnsavedChanges(false);
    setShowUnsavedChangesModal(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  };
  
  const handleSaveAndNavigate = async () => {
    await handleSave();
    setShowUnsavedChangesModal(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  };
  
  // Navigation blocker for page navigation (only if useBlocker is available)
  // Note: useBlocker might not be available in all React Router versions
  // If not available, we'll handle navigation via beforeunload and manual checks
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges && canEdit) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges, canEdit]);

  // Handle delete click - open modal
  const handleDeleteClick = (device: Device) => {
    setDeviceToDelete(device);
    setShowDeleteModal(true);
  };

  // Confirm delete device
  const confirmDelete = async () => {
    if (!deviceToDelete) return;

    setDeletingDeviceId(deviceToDelete.id);
    try {
      await apiService.deleteDevice(deviceToDelete.id);
      toast.success(t('toast.deviceDeleted'));
      queryClient.invalidateQueries({ queryKey: ['user-devices', id] });
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.invalidateQueries({ queryKey: ['devices-count'] });
      setShowDeleteModal(false);
      setDeviceToDelete(null);
    } catch (err: any) {
      toast.error(err.response?.data?.error || t('toast.deviceDeleteFailed'));
    } finally {
      setDeletingDeviceId(null);
    }
  };
  
  // Map backend phase to one of the three step messages (driven by SSE from backend)
  const deleteStepMessage = ((): string | null => {
    if (!deletePhase) return null;
    if (['files', 'folders', 'asset_permissions'].includes(deletePhase)) return t('deleteUserModal.stepRemovingFiles');
    if (['policies', 'certificates', 'refs'].includes(deletePhase)) return t('deleteUserModal.stepPolicies');
    if (['tokens', 'user', 'commit', 'devices', 'keys_started'].includes(deletePhase)) return t('deleteUserModal.stepCleaningUp');
    return null; // e.g. "done"
  })();

  // Progress bar percentage from backend phase (0–100)
  const DELETE_PHASES = ['files', 'folders', 'asset_permissions', 'policies', 'certificates', 'refs', 'tokens', 'user', 'commit', 'devices', 'keys_started', 'done'];
  const deleteProgressPercent = deletePhase ? (DELETE_PHASES.indexOf(deletePhase) + 1) / DELETE_PHASES.length * 100 : 0;

  // Confirm delete user (streaming progress from backend)
  const confirmDeleteUser = async () => {
    if (!user || !id) return;
    setIsDeletingUser(true);
    setDeletePhase(null);
    try {
      await apiService.deleteUserStreaming(id, (phase) => setDeletePhase(phase));
      toast.success(t('toast.userDeleted'));
      queryClient.invalidateQueries({ queryKey: ['users'] });
      navigate('/users');
    } catch (err: any) {
      toast.error(err?.message || err?.response?.data?.error || t('toast.userDeleteFailed'));
    } finally {
      setIsDeletingUser(false);
      setDeletePhase(null);
      setShowDeleteUserModal(false);
    }
  };

  const isLoading = userLoading || devicesLoading || foldersLoading;

  if (userError) {
    return (
      <div className="p-6">
        <div className="card text-center py-12">
          <XCircleIcon className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('error.title')}</h3>
          <p className="mt-1 text-sm text-gray-500">
            {userError instanceof Error ? userError.message : t('error.description')}
          </p>
      
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">


      {/* Loading skeleton */}
      {isLoading ? (
        <div className="space-y-6">
          <div className="card animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          </div>
        </div>
      ) : user ? (
        <>
          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => handleTabChange('details')}
                className={`${
                  activeTab === 'details'
                    ? 'border-governate-500 text-governate-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                {t('tabs.details')}
              </button>
              <button
                onClick={() => handleTabChange('devices')}
                className={`${
                  activeTab === 'devices'
                    ? 'border-governate-500 text-governate-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                {t('tabs.devicesCount', { count: userDevices.length })}
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'details' ? (
            <div className="card">
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">{t('userInfo.title')}</h2>
                {canEdit && !(currentUser?.role === 'sysadmin' && (user?.role === 'sysadmin' || user?.role?.startsWith?.('sysadmin')) && user?.id !== currentUser?.id) && (
                <button
                    onClick={() => setShowDeleteUserModal(true)}
                    className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-md hover:bg-red-50 transition-colors flex items-center gap-2"
                >
                    <TrashIcon className="w-4 h-4" />
                    {t('userInfo.deleteUser')}
                </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('fields.name')}</label>
                  {canEdit ? (
                    <input
                      type="text"
                      value={editFormData.name}
                      onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-governate-500 focus:border-governate-500"
                      placeholder={t('placeholders.enterName')}
                    />
                  ) : (
                  <p className="text-sm text-gray-900">{user.name || t('na')}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('fields.email')}</label>
                  {canEdit ? (
                    <input
                      type="email"
                      value={editFormData.email}
                      onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-governate-500 focus:border-governate-500"
                      placeholder={t('placeholders.enterEmail')}
                    />
                  ) : (
                  <p className="text-sm text-gray-900">{user.email}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('fields.role')}</label>
                  {canEdit && currentUser?.role === 'sysadmin' && ((user?.role !== 'sysadmin' && !user?.role?.startsWith?.('sysadmin')) || user?.id === currentUser?.id) ? (
                    <div className="space-y-2">
                      <select
                        value={editFormData.role}
                        onChange={(e) => {
                          const newRole = e.target.value as any;
                          setEditFormData({ 
                            ...editFormData, 
                            role: newRole,
                            translatorLanguage: newRole === 'translator' ? editFormData.translatorLanguage || 'en' : ''
                          });
                        }}
                        className="w-32 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-governate-500 focus:border-governate-500"
                      >
                        <option value="user">{t('roles.user')}</option>
                        <option value="admin">{t('roles.admin')}</option>
                        <option value="sysadmin">{t('roles.sysadmin')}</option>
                        <option value="translator">{t('roles.translator')}</option>
                      </select>
                      {editFormData.role === 'translator' && (
                        <select
                          value={editFormData.translatorLanguage}
                          onChange={(e) => setEditFormData({ ...editFormData, translatorLanguage: e.target.value as any })}
                          className="w-32 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-governate-500 focus:border-governate-500"
                        >
                          <option value="">{t('selectLanguage')}</option>
                          <option value="en">{t('languages.en')}</option>
                          <option value="de">{t('languages.de')}</option>
                          <option value="es">{t('languages.es')}</option>
                          <option value="fr">{t('languages.fr')}</option>
                          <option value="fi">{t('languages.fi')}</option>
                          <option value="tr">{t('languages.tr')}</option>
                        </select>
                      )}
                    </div>
                  ) : canEdit ? (
                    <select
                      value={editFormData.role}
                      onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value as any })}
                      className="w-32 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-governate-500 focus:border-governate-500"
                    >
                      <option value="user">{t('roles.user')}</option>
                      <option value="admin">{t('roles.admin')}</option>
                    </select>
                  ) : (
                    <p className="text-sm text-gray-900 capitalize">
                      {user.role && user.role.startsWith('translator_') 
                        ? t('roles.translatorWithLang', { lang: user.role.replace('translator_', '').toUpperCase() })
                        : user.role}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('fields.mode')}</label>
                  {(() => {
                    const fullAvail = (companyUsages?.full_licenses_max ?? 0) - (companyUsages?.full_licenses_used ?? 0);
                    const readerAvail = (companyUsages?.reader_licenses_max ?? 0) - (companyUsages?.reader_licenses_used ?? 0);
                    const canSetFull = user.company_id ? (user.mode === 'full' || fullAvail > 0) : true;
                    const canSetReader = user.company_id ? (user.mode === 'reader' || readerAvail > 0) : true;
                    const canEditMode = canEditModeAndStatus && (user.company_id ? (canSetFull || canSetReader) : true);
                    return canEditMode ? (
                      <select
                        value={editFormData.mode}
                        onChange={(e) => setEditFormData({ ...editFormData, mode: e.target.value as 'full' | 'reader' })}
                        className="w-32 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-governate-500 focus:border-governate-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                      >
                        <option value="full" disabled={!canSetFull}>{t('mode.full')}</option>
                        <option value="reader" disabled={!canSetReader}>{t('mode.reader')}</option>
                      </select>
                    ) : (
                      <p className="text-sm text-gray-900 dark:text-white capitalize">
                        {user.mode === 'reader' ? t('mode.reader') : t('mode.full')}
                      </p>
                    );
                  })()}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('fields.status')}</label>
                  {canEditModeAndStatus ? (
                    <div>
                      <label className={`relative inline-flex items-center ${!user.is_active && user.can_activate === false ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          checked={editFormData.is_active}
                          onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.checked })}
                          className="sr-only peer"
                          disabled={!user.is_active && user.can_activate === false}
                        />
                        <div className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-governate-300 dark:peer-focus:ring-governate-800 dark:bg-gray-700 peer-checked:bg-governate-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-disabled:opacity-50"></div>
                        <span className="ml-3 text-sm text-gray-700">
                          {editFormData.is_active ? t('statusActive') : t('statusInactive')}
                        </span>
                      </label>
                      {!user.is_active && user.can_activate === false && (
                        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                          {t('noLicenseToActivate')}
                        </p>
                      )}
                    </div>
                  ) : (
                  <div className="flex items-center">
                    {user.is_active ? (
                      <>
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                        <span className="text-sm text-green-600">{t('statusActive')}</span>
                      </>
                    ) : (
                      <>
                        <XCircleIcon className="w-5 h-5 text-red-500 mr-2" />
                        <span className="text-sm text-red-600">{t('statusInactive')}</span>
                      </>
                    )}
                  </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('fields.company')}</label>
                  <div className="flex items-center gap-2">
                    {user.company_id ? (
                      user.company_name ? (
                        <button
                          onClick={() => navigate(`/clients/${user.company_id}`)}
                          className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {user.company_name}
                        </button>
                      ) : (
                        <p className="text-sm text-gray-900">{t('na')}</p>
                      )
                    ) : (
                      <p className="text-sm text-gray-900 dark:text-gray-100">{t('noCompany')}</p>
                    )}
                    {(currentUser?.role === 'sysadmin' || (currentUser?.role === 'admin' && canEdit)) && (
                      <button
                        onClick={() => {
                          setShowCompanyEditModal(true);
                          fetchCompanies();
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        title={t('editCompany')}
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('fields.emailVerified')}</label>
                  <div className="flex items-center">
                    {user.is_verified ? (
                      <>
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                        <span className="text-sm text-green-600">{t('verified')}</span>
                      </>
                    ) : (
                      <>
                        <XCircleIcon className="w-5 h-5 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-500">{t('notVerified')}</span>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('fields.createdAt')}</label>
                  <p className="text-sm text-gray-900">
                    {user.created_at ? (() => {
                      const date = new Date(user.created_at);
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const hours = String(date.getHours()).padStart(2, '0');
                      const minutes = String(date.getMinutes()).padStart(2, '0');
                      const seconds = String(date.getSeconds()).padStart(2, '0');
                      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
                    })() : t('na')}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('fields.lastLogin')}</label>
                  <p className="text-sm text-gray-900 flex items-center">
                    <ClockIcon className="w-4 h-4 mr-2 text-gray-400" />
                    {user.last_login ? (() => {
                      const date = new Date(user.last_login);
                      // Check if date is valid (not NaN and not an invalid date like year 1)
                      if (isNaN(date.getTime()) || date.getFullYear() < 1900) {
                        return t('never');
                      }
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const hours = String(date.getHours()).padStart(2, '0');
                      const minutes = String(date.getMinutes()).padStart(2, '0');
                      const seconds = String(date.getSeconds()).padStart(2, '0');
                      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
                    })() : t('never')}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">{t('fields.passwordLastUpdated')}</label>
                  <p className="text-sm text-gray-900 flex items-center">
                    <ClockIcon className="w-4 h-4 mr-2 text-gray-400" />
                    {user.password_last_updated_at ? (() => {
                      const date = new Date(user.password_last_updated_at);
                      // Check if date is valid (not NaN and not an invalid date like year 1)
                      if (isNaN(date.getTime()) || date.getFullYear() < 1900) {
                        return t('never');
                      }
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const hours = String(date.getHours()).padStart(2, '0');
                      const minutes = String(date.getMinutes()).padStart(2, '0');
                      const seconds = String(date.getSeconds()).padStart(2, '0');
                      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
                    })() : t('never')}
                  </p>
                </div>
                {/* Ownership Transfer Enabled - Only visible to admins */}
                {(currentUser?.role === 'sysadmin' || currentUser?.role === 'admin') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      {t('fields.ownershipTransferEnabled')}
                    </label>
                    {preferencesLoading ? (
                      <p className="text-sm text-gray-500">{t('loading')}</p>
                    ) : (
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={userPreferences?.ownership_transfer_enabled ?? true}
                          onChange={async (e) => {
                            if (!id) return;
                            const newValue = e.target.checked;
                            setUserPreferences({ ...userPreferences, ownership_transfer_enabled: newValue });
                            
                            // Save immediately
                            try {
                              setIsUpdatingPreferences(true);
                              await apiService.updateUserPreferencesForUser(id, {
                                ownership_transfer_enabled: newValue,
                              });
                              setOriginalPreferences({ ...userPreferences, ownership_transfer_enabled: newValue });
                              toast.success(t('toast.ownershipTransferUpdated'));
                              queryClient.invalidateQueries({ queryKey: ['user-preferences', id] });
                              // If admin updated the current user's preference, refresh profile so Assets/AssetEdit see the new value
                              if (currentUser?.id === id) refreshUserData();
                            } catch (error: any) {
                              // Revert on error
                              setUserPreferences(originalPreferences);
                              toast.error(error.response?.data?.error || t('toast.ownershipTransferFailed'));
                            } finally {
                              setIsUpdatingPreferences(false);
                            }
                          }}
                          disabled={isUpdatingPreferences}
                          className="sr-only peer"
                        />
                        <div className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-governate-300 dark:peer-focus:ring-governate-800 dark:bg-gray-700 peer-checked:bg-governate-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
                        <span className="ml-3 text-sm text-gray-700">
                          {userPreferences?.ownership_transfer_enabled ? t('enabled') : t('disabled')}
                        </span>
                      </label>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {t('ownershipTransferHint')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {userDevices.length === 0 ? (
                <div className="card text-center py-12">
                  <DevicePhoneMobileIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">{t('devices.noDevices')}</h3>
                  <p className="mt-1 text-sm text-gray-500">{t('devices.noDevicesDescription')}</p>
                </div>
              ) : (
                userDevices.map((device: Device) => {
                  const deviceFolders = foldersByDevice[device.id] || [];
                  const DeviceIcon = device.type === 'mobile' ? DevicePhoneMobileIcon : ComputerDesktopIcon;

                  return (
                    <div key={device.id} className="card">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <DeviceIcon className="w-8 h-8 text-gray-600" />
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">{device.name}</h3>
                            <p className="text-sm text-gray-500 capitalize">
                              {device.type}
                              {device.system_info?.os ? ` • ${device.system_info.os}` : ''}
                              {device.system_info?.os_version ? ` ${device.system_info.os_version}` : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <p className="text-xs text-gray-500">
                              {device.last_seen
                                ? `${t('devices.lastSeen')} ${new Date(device.last_seen).toLocaleString()}`
                                : `${t('devices.created')} ${new Date(device.created_at).toLocaleString()}`}
                            </p>
                          </div>
                          <button
                            onClick={() => handleDeleteClick(device)}
                            disabled={deletingDeviceId === device.id}
                            className="inline-flex items-center p-2 border border-transparent rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={t('devices.deleteDevice')}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Folders for this device */}
                      {deviceFolders.length > 0 ? (
                        <div className="mt-4 border-t border-gray-200 pt-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-3">
                            {t('devices.folders', { count: deviceFolders.length })}
                          </h4>
                          <div className="space-y-2">
                            {deviceFolders.map((folder: AutoEncryptFolder) => (
                              <div
                                key={folder.id}
                                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                              >
                                <div className="flex items-center space-x-3">
                                  <FolderIcon
                                    className={`w-5 h-5 ${
                                      folder.is_active ? 'text-green-500' : 'text-gray-400'
                                    }`}
                                  />
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">
                                      {folder.name || folder.folder_path || t('devices.unnamed')}
                                    </p>
                                    <p className="text-xs text-gray-500">{folder.folder_path}</p>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span
                                    className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                                      folder.is_active
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-gray-100 text-gray-800'
                                    }`}
                                  >
                                    {folder.is_active ? t('statusActive') : t('statusInactive')}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="mt-4 border-t border-gray-200 pt-4">
                          <p className="text-sm text-gray-500">{t('devices.noFolders')}</p>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </>
      ) : null}

      {/* Unsaved Changes Modal */}
      {showUnsavedChangesModal && createPortal(
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75"
              onClick={() => setShowUnsavedChangesModal(false)}
            ></div>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 dark:bg-yellow-900/30 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                      {t('unsavedModal.title')}
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t('unsavedModal.description')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                <button
                  type="button"
                  onClick={handleSaveAndNavigate}
                  className="w-full inline-flex justify-center items-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-governate-600 text-base font-medium text-white hover:bg-governate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-governate-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {t('unsavedModal.saveChanges')}
                </button>
                <button
                  type="button"
                  onClick={handleDiscardChanges}
                  className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-governate-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  {t('unsavedModal.discard')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowUnsavedChangesModal(false);
                    setPendingNavigation(null);
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-governate-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  {t('unsavedModal.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete User Confirmation Modal */}
      {showDeleteUserModal && user && createPortal(
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75"
              onClick={() => !isDeletingUser && setShowDeleteUserModal(false)}
            ></div>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                      {t('deleteUserModal.title')}
                    </h3>
                    <div className="mt-2">
                      {isDeletingUser ? (
                        <>
                          {deleteStepMessage && (
                            <p className="text-sm text-governate-600 dark:text-governate-400">
                              {deleteStepMessage}
                            </p>
                          )}
                          <div className="mt-3 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
                            <div
                              className="h-full bg-red-600 transition-all duration-300 ease-out"
                              style={{ width: `${deleteProgressPercent}%` }}
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {t('deleteUserModal.descriptionBefore')}<strong>{user.name || user.email}</strong>{t('deleteUserModal.descriptionAfter')}
                          </p>
                          {user.company_id && (
                            <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900 rounded-lg">
                              <p className="text-sm text-amber-800 dark:text-amber-200 font-medium">{t('deleteUserModal.companyUserNote')}</p>
                              <ul className="text-sm text-amber-700 dark:text-amber-300 mt-1 ml-4 list-disc">
                                {companyAssetProtection?.keys_backup_strategy?.when_user_leaves?.transfer_ownership_to_admin_on_user_leave ? (
                                  <li>{t('deleteUserModal.companyUserTransferEnabled')}</li>
                                ) : (
                                  <li>{t('deleteUserModal.companyUserTransferDisabled')}</li>
                                )}
                                <li>{t('deleteUserModal.keyBackupOnlySelf')}</li>
                              </ul>
                            </div>
                          )}
                          <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900 rounded-lg">
                            <p className="text-sm text-red-800 dark:text-red-200 font-medium">⚠️ {t('deleteUserModal.warning')}</p>
                            <ul className="text-sm text-red-700 dark:text-red-300 mt-1 ml-4 list-disc">
                              <li>{t('deleteUserModal.warningLine1')}</li>
                              <li>{t('deleteUserModal.warningLine2')}</li>
                            </ul>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                <button
                  type="button"
                  onClick={confirmDeleteUser}
                  disabled={isDeletingUser}
                  className="w-full inline-flex justify-center items-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {isDeletingUser ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      {t('deleteUserModal.deleting')}
                    </>
                  ) : (
                    t('deleteUserModal.delete')
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteUserModal(false)}
                  disabled={isDeletingUser}
                  className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-governate-500 disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:w-auto sm:text-sm"
                >
                  {t('deleteUserModal.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Device Confirmation Modal */}
      {showDeleteModal && deviceToDelete && createPortal(
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75"
              onClick={() => !deletingDeviceId && setShowDeleteModal(false)}
            ></div>

            {/* Modal */}
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                      {t('deleteDeviceModal.title')}
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t('deleteDeviceModal.descriptionBefore')}<strong>{deviceToDelete.name}</strong>{t('deleteDeviceModal.descriptionAfter')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                <button
                  type="button"
                  onClick={confirmDelete}
                  disabled={deletingDeviceId === deviceToDelete.id}
                  className="w-full inline-flex justify-center items-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {deletingDeviceId === deviceToDelete.id ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      {t('deleteDeviceModal.deleting')}
                    </>
                  ) : (
                    t('deleteDeviceModal.delete')
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeviceToDelete(null);
                  }}
                  disabled={deletingDeviceId === deviceToDelete.id}
                  className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-governate-500 disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:w-auto sm:text-sm"
                >
                  {t('deleteDeviceModal.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Company Edit Modal */}
      {showCompanyEditModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('companyModal.title')}</h3>
            </div>
            <div className="px-6 py-4 flex-1 overflow-y-auto">
              {/* Search */}
              <div className="mb-4">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5" />
                  <input
                    type="text"
                    placeholder={t('companyModal.searchPlaceholder')}
                    value={companySearchTerm}
                    onChange={(e) => setCompanySearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Company List */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {/* Option to remove company */}
                <button
                  onClick={() => handleCompanyUpdate(null)}
                  disabled={isUpdatingCompany}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-colors ${
                    !user?.company_id
                      ? 'border-governate-500 bg-governate-50 dark:bg-governate-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <div className="flex items-center gap-3">
                    <BuildingOfficeIcon className="w-5 h-5 text-gray-400" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">{t('companyModal.noCompany')}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{t('companyModal.removeCompany')}</div>
                    </div>
                  </div>
                </button>

                {filteredCompanies.map((company: any) => (
                  <button
                    key={company.id}
                    onClick={() => handleCompanyUpdate(company.id)}
                    disabled={isUpdatingCompany}
                    className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-colors ${
                      user?.company_id === company.id
                        ? 'border-governate-500 bg-governate-50 dark:bg-governate-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <div className="flex items-center gap-3">
                      <BuildingOfficeIcon className="w-5 h-5 text-gray-400" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{company.name}</div>
                        {company.domain && (
                          <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{company.domain}</div>
                        )}
                      </div>
                      {user?.company_id === company.id && (
                        <CheckCircleIcon className="w-5 h-5 text-governate-500 flex-shrink-0" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => {
                  setShowCompanyEditModal(false);
                  setCompanySearchTerm('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {t('companyModal.cancel')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default UserDetails;

